import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { Feed, Member } from '@pacer/shared';
import { MEMBER_COLORS } from '@pacer/shared';
import * as api from '../api';
import { DisplaySizeSwitcher } from '../components/DisplaySizeSwitcher';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { ThemeSwitcher } from '../components/ThemeSwitcher';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Field,
  IconButton,
  Input,
  PageHeader,
  Select,
  cx,
} from '../components/ui';
import { CategoryPanel } from './settings/CategoryPanel';
import { NotificationsPanel } from './settings/NotificationsPanel';
import { ScreensaverPanel } from './settings/ScreensaverPanel';
import { WeatherPanel } from './settings/WeatherPanel';
import { queryKeys } from '../state/queries';
import { setActiveMember, useActiveMemberId } from '../state/activeMember';
import { confirmDelete } from '../state/ui';

export function Settings() {
  const qc = useQueryClient();
  const activeId = useActiveMemberId();
  const [editingMember, setEditingMember] = useState<Member | 'new' | null>(null);
  const [editingFeed, setEditingFeed] = useState<Feed | 'new' | null>(null);

  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });
  const { data: household } = useQuery({ queryKey: queryKeys.household, queryFn: api.getHousehold });
  const { data: feeds = [] } = useQuery({ queryKey: queryKeys.feeds, queryFn: api.getFeeds });

  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      setActiveMember(null);
      qc.clear();
      location.reload();
    },
  });

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 md:px-8 md:py-10">
      <PageHeader eyebrow="Household" title="Settings" actions={<ThemeSwitcher />} />

      <div className="flex flex-col gap-10">
        {/* --- Display --- */}
        <section className="flex flex-col gap-3">
          <h2 className="font-display text-[20px] text-primary">Display</h2>
          <Card className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="min-w-0">
              <span className="text-[14px] text-primary">Display size</span>
              <span className="block font-mono text-[12px] text-muted">
                Scales the whole app — larger to read from across the room.
              </span>
            </div>
            <DisplaySizeSwitcher />
          </Card>
        </section>

        {/* --- Family --- */}
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-[20px] text-primary">Family</h2>
            <Button variant="primary" size="sm" icon="plus" className="ml-auto" onClick={() => setEditingMember('new')}>
              Add person
            </Button>
          </div>

          {members.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border-subtle px-4 py-8 text-center font-mono text-[12px] text-muted">
              No one yet. Add each family member so events and chores can be assigned.
            </p>
          ) : (
            <Card className="overflow-hidden">
              <ul>
                {members.map((m) => (
                  <li key={m.id} className="border-b border-border-subtle last:border-b-0">
                    <button
                      onClick={() => setEditingMember(m)}
                      className="touch flex w-full items-center gap-3 px-3 text-left transition-colors hover:bg-surface-high/40"
                    >
                      <Avatar name={m.name} color={m.color} photoUrl={m.avatarUrl} size={36} />
                      <span className="flex-1">
                        <span className="block text-[15px] text-primary">{m.name}</span>
                        {m.birthday && (
                          <span className="tabular flex items-center gap-1 font-mono text-[12px] text-muted">
                            <Icon name="cake" size={11} />
                            {m.birthday}
                          </span>
                        )}
                      </span>
                      {m.id === activeId && <Badge tone="accent">You</Badge>}
                      <Icon name="chevron-right" size={14} className="text-muted" />
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </section>

        {/* --- Subscribed calendars --- */}
        <section className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-display text-[20px] text-primary">Subscribed calendars</h2>
            <Button variant="primary" size="sm" icon="plus" className="ml-auto" onClick={() => setEditingFeed('new')}>
              Add calendar
            </Button>
          </div>
          <p className="text-[13px] leading-relaxed text-secondary">
            Pull in everyone's existing Google, Apple or Outlook calendar. They show on the wall
            read-only and refresh automatically — no account login needed, just each calendar's
            private iCal address.
          </p>

          {feeds.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border-subtle px-4 py-8 text-center font-mono text-[12px] text-muted">
              No calendars subscribed.
            </p>
          ) : (
            <Card className="overflow-hidden">
              <ul>
                {feeds.map((f) => (
                  <FeedRow key={f.id} feed={f} members={members} onEdit={() => setEditingFeed(f)} />
                ))}
              </ul>
            </Card>
          )}
        </section>

        <CategoryPanel />

        {/* --- Outbound feed --- */}
        {household?.icalToken && <PublishPanel token={household.icalToken} />}

        <NotificationsPanel />
        <WeatherPanel household={household} />
        <ScreensaverPanel household={household} />

        {/* --- Household --- */}
        <section className="flex flex-col gap-3">
          <h2 className="font-display text-[20px] text-primary">This household</h2>
          <Card className="flex flex-col gap-3 p-4">
            <Row label="Name" value={household?.name ?? '—'} />
            <Row label="Timezone" value={household?.timezone ?? '—'} />
            <p className="font-mono text-[12px] leading-relaxed text-muted">
              The timezone drives the wall clock and all-day boundaries. Everyone sees these times,
              wherever they happen to be.
            </p>
          </Card>
        </section>

        {/* --- Device --- */}
        <section className="flex flex-col gap-3">
          <h2 className="font-display text-[20px] text-primary">This device</h2>
          <Button variant="danger" onClick={() => logout.mutate()} className="self-start">
            Sign out
          </Button>
          <p className="font-mono text-[12px] text-muted">
            Signs out this device only. The wall tablet should normally stay signed in.
          </p>
        </section>
      </div>

      {editingMember && (
        <MemberEditor
          member={editingMember === 'new' ? null : editingMember}
          onClose={() => setEditingMember(null)}
        />
      )}
      {editingFeed && (
        <FeedEditor
          feed={editingFeed === 'new' ? null : editingFeed}
          members={members}
          onClose={() => setEditingFeed(null)}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="eyebrow" style={{ letterSpacing: '0.18em' }}>
        {label}
      </span>
      <span className="text-[14px] text-primary">{value}</span>
    </div>
  );
}

/** The outbound half: subscribe once from a phone, family events appear there forever. */
function PublishPanel({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${location.origin}/ical/household.ics?token=${token}`;

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard needs a secure context; the field is selectable as a fallback.
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-display text-[20px] text-primary">Your family feed</h2>
      <p className="text-[13px] leading-relaxed text-secondary">
        Subscribe to this address in Google, Apple or Outlook Calendar and every Pacer event shows up
        there automatically. Add it once per phone.
      </p>
      <Card className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} className="font-mono !text-[12px]" />
          <Button variant="secondary" icon={copied ? 'check' : 'copy'} onClick={copy} className="shrink-0">
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="font-mono text-[12px] leading-relaxed text-muted">
          Anyone with this link can read the family calendar — treat it like a password. It works
          because calendar apps can't log in.
        </p>
      </Card>
    </section>
  );
}

function FeedRow({ feed, members, onEdit }: { feed: Feed; members: Member[]; onEdit: () => void }) {
  const qc = useQueryClient();
  const owner = members.find((m) => m.id === feed.memberId);

  const refresh = useMutation({
    mutationFn: () => api.refreshFeed(feed.id),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.feeds });
      await qc.invalidateQueries({ queryKey: ['events'] });
    },
  });

  return (
    <li className="flex items-center gap-3 border-b border-border-subtle px-3 py-2 last:border-b-0">
      <span
        className="size-2.5 shrink-0 rounded-full"
        style={{ background: feed.color ?? owner?.color ?? 'var(--color-text-muted)' }}
      />
      <button onClick={onEdit} className="min-w-0 flex-1 py-1 text-left">
        <span className="block truncate text-[14px] text-primary">{feed.name}</span>
        <span className="tabular block truncate font-mono text-[12px] text-muted">
          {feed.lastError ? (
            <span className="text-danger">{feed.lastError}</span>
          ) : (
            <>
              {feed.eventCount} events
              {feed.lastFetchedAt &&
                ` · synced ${DateTime.fromISO(feed.lastFetchedAt).toRelative({ style: 'short' })}`}
            </>
          )}
        </span>
      </button>

      {feed.lastError && <Icon name="alert" size={14} className="shrink-0 text-danger" />}
      {!feed.enabled && <Badge>Off</Badge>}
      {owner && <Avatar name={owner.name} color={owner.color} photoUrl={owner.avatarUrl} size={24} />}

      <IconButton
        icon="refresh"
        label={`Sync ${feed.name}`}
        size="sm"
        onClick={() => refresh.mutate()}
        disabled={refresh.isPending}
        className={refresh.isPending ? 'animate-spin' : undefined}
      />
    </li>
  );
}

function FeedEditor({
  feed,
  members,
  onClose,
}: {
  feed: Feed | null;
  members: Member[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(feed?.name ?? '');
  const [url, setUrl] = useState(feed?.url ?? '');
  const [memberId, setMemberId] = useState(feed?.memberId ?? '');
  const [color, setColor] = useState<string | null>(feed?.color ?? null);
  const [enabled, setEnabled] = useState(feed?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  const done = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: queryKeys.feeds });
    await qc.invalidateQueries({ queryKey: ['events'] });
    onClose();
  };

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      const payload = { name: name.trim(), url: url.trim(), memberId: memberId || null, color, enabled };
      // Create validates the URL by fetching it, so a bad address surfaces here as an error.
      if (feed) await api.updateFeed(feed.id, payload);
      else await api.createFeed(payload);
    },
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteFeed(feed!.id),
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={feed ? 'Edit' : 'Subscribe'}
      title={feed ? 'Edit calendar' : 'Add a calendar'}
      footer={
        <>
          {feed && (
            <Button
              variant="danger"
              onClick={async () => {
                if (await confirmDelete(`the “${feed.name}” calendar`)) remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Remove
            </Button>
          )}
          <Button
            variant="primary"
            className="ml-auto min-w-24"
            onClick={() => save.mutate()}
            disabled={!name.trim() || !url.trim() || save.isPending}
          >
            {save.isPending ? 'Checking…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Alex's work calendar"
            autoCapitalize="words"
          />
        </Field>

        <Field
          label="Private iCal address"
          hint="Google Calendar → Settings → your calendar → 'Secret address in iCal format'."
        >
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
            inputMode="url"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            className="font-mono !text-[12px]"
          />
        </Field>

        <Field label="Belongs to">
          <Select value={memberId} onChange={(e) => setMemberId(e.target.value)}>
            <option value="">Nobody in particular</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Colour" hint="Auto uses the owner's colour.">
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              onClick={() => setColor(null)}
              className={cx(
                'h-8 rounded-md border px-3 font-mono text-[12px] uppercase transition-colors',
                color === null ? 'border-accent text-accent' : 'border-border text-muted',
              )}
              style={{ letterSpacing: '0.12em' }}
            >
              Auto
            </button>
            {MEMBER_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className="size-8 rounded-full"
                style={{ background: c, outline: color === c ? `2px solid ${c}` : 'none', outlineOffset: 3 }}
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>
        </Field>

        <label className="flex touch items-center gap-3">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="size-5 accent-[var(--color-accent)]"
          />
          <span className="text-[14px] text-secondary">Show on the calendar</span>
        </label>

        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

function MemberEditor({ member, onClose }: { member: Member | null; onClose: () => void }) {
  const qc = useQueryClient();
  const activeId = useActiveMemberId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(member?.name ?? '');
  const [birthday, setBirthday] = useState(member?.birthday ?? '');
  const [color, setColor] = useState<string>(member?.color ?? MEMBER_COLORS[0]);
  // Pending photo change, applied on Save. `remove` clears an existing photo.
  const [avatar, setAvatar] = useState<{ preview: string; data: string } | { remove: true } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentPhoto =
    avatar && 'preview' in avatar ? avatar.preview : avatar && 'remove' in avatar ? null : (member?.avatarUrl ?? null);

  const pickFile = (file: File | undefined): void => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setAvatar({ preview: String(reader.result), data: String(reader.result) });
    reader.onerror = () => setError(`Could not read ${file.name}`);
    reader.readAsDataURL(file);
  };

  const done = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: queryKeys.members });
    onClose();
  };

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      const payload = { name: name.trim(), color, birthday: birthday || null };
      // Create returns the new id, which the avatar upload needs — so a brand-new person
      // and their photo land in one Save.
      const id = member ? (await api.updateMember(member.id, payload), member.id) : (await api.createMember(payload)).id;
      if (avatar && 'data' in avatar) await api.setMemberAvatar(id, avatar.data);
      else if (avatar && 'remove' in avatar && member) await api.removeMemberAvatar(id);
    },
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteMember(member!.id),
    onSuccess: async () => {
      // Clear the tapped profile if this device was acting as the deleted person.
      if (activeId === member!.id) setActiveMember(null);
      await done();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={member ? 'Edit' : 'New'}
      title={member ? 'Edit person' : 'Add person'}
      footer={
        <>
          {member && (
            <Button
              variant="danger"
              onClick={async () => {
                if (await confirmDelete(member.name, 'Their events stay on the calendar — they just stop being assigned.'))
                  remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Remove
            </Button>
          )}
          <Button
            variant="primary"
            className="ml-auto min-w-24"
            onClick={() => save.mutate()}
            disabled={!name.trim() || save.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-2 py-2">
          <button
            onClick={() => fileInput.current?.click()}
            className="group relative rounded-full"
            aria-label="Change photo"
          >
            <Avatar name={name || '?'} color={color} photoUrl={currentPhoto} size={72} />
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 opacity-0 transition-opacity group-hover:opacity-100">
              <Icon name="camera" size={20} className="text-white" />
            </span>
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fileInput.current?.click()}
              className="font-mono text-[12px] text-accent uppercase"
              style={{ letterSpacing: '0.12em' }}
            >
              {currentPhoto ? 'Change photo' : 'Add photo'}
            </button>
            {currentPhoto && (
              <button
                onClick={() => setAvatar({ remove: true })}
                className="font-mono text-[12px] text-muted uppercase"
                style={{ letterSpacing: '0.12em' }}
              >
                Remove
              </button>
            )}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
        </div>

        <Field label="Name" hint="Used for the monogram when there's no photo.">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Alex"
            autoCapitalize="words"
            enterKeyHint="done"
          />
        </Field>

        <Field label="Birthday">
          <Input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
        </Field>

        <Field label="Colour">
          <div className="flex flex-wrap gap-2 pt-1">
            {MEMBER_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className="size-9 rounded-full"
                style={{ background: c, outline: color === c ? `2px solid ${c}` : 'none', outlineOffset: 3 }}
                aria-label={`Colour ${c}`}
              />
            ))}
          </div>
        </Field>

        {error && <p className="font-mono text-[12px] text-danger">{error}</p>}

        {member && <LocationWebhook member={member} />}

        {member && (
          <p className="font-mono text-[12px] leading-relaxed text-muted">
            Removing someone keeps their events on the calendar — they just stop being assigned.
          </p>
        )}
      </div>
    </Modal>
  );
}

/** Shows the member's private location-webhook URL to paste into an arrive/leave automation. */
function LocationWebhook({ member }: { member: Member }) {
  const [copied, setCopied] = useState(false);
  // Placeholders make the Shortcuts step obvious; the real lat/lon are filled by the phone.
  const url = `${location.origin}/loc/${member.locToken}?lat=LAT&lon=LON`;

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard needs a secure context; the field is selectable as a fallback */
    }
  };

  return (
    <details className="rounded-lg border border-border-subtle bg-surface/40 p-3">
      <summary className="cursor-pointer font-mono text-[12px] tracking-wide text-secondary uppercase">
        Location automation
      </summary>
      <div className="mt-3 flex flex-col gap-2">
        <p className="text-[12px] leading-relaxed text-secondary">
          Post {member.name}'s location automatically when they arrive or leave a place, using an
          Apple Shortcuts automation (or OwnTracks). Their private webhook:
        </p>
        <div className="flex items-center gap-2">
          <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} className="font-mono !text-[12px]" />
          <Button variant="secondary" icon={copied ? 'check' : 'copy'} onClick={copy} className="shrink-0">
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="font-mono text-[11px] leading-relaxed text-muted">
          In Shortcuts → Automation → “When I arrive/leave”: Get Current Location → make a URL with
          this, replacing LAT/LON with the location's Latitude and Longitude → Get Contents of URL
          (POST). Treat this link like a password.
        </p>
      </div>
    </details>
  );
}
